import { useEffect, useRef } from 'react'
import { cn } from 'src/utils/style'
import type { HeaderCheckboxState } from 'src/hooks/use-bulk-selection'

interface TriStateCheckboxProps {
  /** Visual state. `indeterminate` is applied via the DOM property, not an attribute. */
  state: HeaderCheckboxState
  onChange: () => void
  className?: string
  'aria-label'?: string
  'data-testid'?: string
}

/**
 * A checkbox that can render the three selection states a bulk list needs:
 * unchecked, indeterminate (some rows on the page selected), and checked.
 * Used for both the header (all three states) and rows (checked/unchecked).
 */
export const TriStateCheckbox = ({
  state,
  onChange,
  className,
  ...rest
}: TriStateCheckboxProps) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate'
  }, [state])

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'checked'}
      onChange={onChange}
      // Rows live inside a clickable card; don't let the checkbox open the row.
      onClick={(e) => e.stopPropagation()}
      className={cn(['h-4 w-4 cursor-pointer accent-primary', className])}
      {...rest}
    />
  )
}
